import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AddPlan from "./components/Modals/AddPlan";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useAppServices } from "../../../../../hook/services";

export default function UIConfiguration() {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { companyId } = useParams();
  const [plansCustomization, setPlansCustomization] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDuplicateMode, setIsDuplicateMode] = useState(false);

  const handleNavigate = (planId) => {
    navigate(`./SaaS/${planId}`);
  };

  const getPlansCustomization = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.sidenav_customizer_saas_level.get({
        query: `companyId=${companyId}`,
      });
      setPlansCustomization(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch Customizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAgencyPlans = async () => {
    try {
      const { response } =
        await AppService.sidenav_customizer_saas_level.getPlans({
          query: `company_id=${companyId}`,
        });

      if (response && Array.isArray(response.data)) {
        const mappedPlans = response.data.map((plan) => ({
          value: plan.id,
          label: plan.name,
        }));

        setPlans(mappedPlans);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error("Error fetching Plans:", error);
      toast.error("Failed to load Plans.");
    }
  };

  const savePlansCustomization = async (customizationData) => {
    const payload = {
      name: customizationData.name,
      companyId: companyId,
      planId: customizationData.plan.value,
      planName: customizationData.plan.label,
      ...(isEditMode && selectedPlan?._id && { _id: selectedPlan._id }),
      ...(isDuplicateMode &&
        selectedPlan?._id && { orignalId: selectedPlan._id }),
    };

    try {
      const { response } = await AppService.sidenav_customizer_saas_level[
        isEditMode ? "update" : isDuplicateMode ? "duplicate" : "create"
      ]({
        payload,
      });
      if (response.success) {
        toast.success(
          `Customization ${
            isEditMode ? "update" : isDuplicateMode ? "duplicate" : "create"
          } successfully!`
        );
        await getPlansCustomization();
        resetPlanState();
      } else {
        toast.error(
          `Failed to ${
            isEditMode ? "update" : isDuplicateMode ? "duplicate" : "create"
          } Customization: ` + response.message
        );
      }
    } catch (error) {
      console.error(
        `Error ${
          isEditMode ? "updating" : isDuplicateMode ? "duplicating" : "creating"
        } Customization:`,
        error
      );
      toast.error("Something went wrong.");
    }
  };

  const deletePlanCustomization = async (planId) => {
    const result = await Swal.fire({
      title: "Delete this customization?",
      text: "This will permanently delete the plan customization. This action cannot be undone.",
      iconHtml:
        '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
      showCancelButton: true,
      confirmButtonText: "Delete customization",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "sc-swal-popup",
        icon: "sc-swal-icon",
        title: "sc-swal-title",
        htmlContainer: "sc-swal-text",
        actions: "sc-swal-actions",
        confirmButton: "sc-swal-btn sc-swal-btn--danger",
        cancelButton: "sc-swal-btn sc-swal-btn--ghost",
      },
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const { response } =
          await AppService.sidenav_customizer_saas_level.delete({
            query: `_id=${planId}`,
          });

        if (response.success) {
          toast.success("Plan customization deleted successfully!");
          await getPlansCustomization();
        } else {
          toast.error(
            "Failed to delete plan customization: " + response.message
          );
        }
      } catch (error) {
        console.error("Error deleting plan customization:", error);
        toast.error("Failed to delete plan customization.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDuplicate = (originalPlan) => {
    setSelectedPlan(originalPlan);
    setIsEditMode(false);
    setIsDuplicateMode(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setIsEditMode(true);
  };

  const resetPlanState = () => {
    setSelectedPlan(null);
    setIsEditMode(false);
  };

  const usedPlanValues = plansCustomization
    .filter((plan) => {
      if (isEditMode) {
        return plan._id !== selectedPlan?._id;
      }
      return true;
    })
    .map((plan) => plan.planId);

  const availablePlans = plans.filter(
    (plan) => !usedPlanValues.includes(plan.value)
  );

  useEffect(() => {
    getPlansCustomization();
    getAgencyPlans();
  }, []);

  return (
    <div
      className="min-vh-100 bg-white text-dark"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="container py-5" style={{ maxWidth: 960 }}>
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="sc-back-btn d-inline-flex align-items-center mb-4 bg-transparent border-0 p-0 text-secondary"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
        </button>

        {/* Header */}
        <header className="mb-4">
          <h1 className="fw-semibold mb-2" style={{ fontSize: "1.625rem", letterSpacing: "-0.01em" }}>
            SaaS plan customizations
          </h1>
          <p className="text-secondary mb-0" style={{ fontSize: "0.9375rem", lineHeight: 1.55 }}>
            Customize the user interface for different SaaS plans within your organization.
          </p>
        </header>

        {/* Add action */}
        <div className="d-flex justify-content-end mb-3">
          <AddPlan
            agencyPlans={availablePlans}
            savePlansCustomization={savePlansCustomization}
            selectedPlan={selectedPlan}
            isEditMode={isEditMode}
            isDuplicate={isDuplicateMode}
            resetPlanState={resetPlanState}
          />
        </div>

        {/* Content */}
        <div className="d-flex flex-column gap-2">
          {loading ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 text-secondary">
              <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
              <span style={{ fontSize: "0.8125rem" }}>Loading customizations…</span>
            </div>
          ) : Array.isArray(plansCustomization) && plansCustomization.length > 0 ? (
            plansCustomization.map((plan, index) => (
              <article key={index} className="sc-card">
                <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <h2 className="sc-card-title mb-0">{plan.name}</h2>
                    </div>
                    {plan.planName && (
                      <div className="sc-plan-badge d-inline-flex align-items-center mt-2">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                        <span className="sc-plan-label">Plan</span>
                        <span className="sc-plan-divider" aria-hidden>·</span>
                        <span className="sc-plan-name text-capitalize">{plan.planName}</span>
                      </div>
                    )}
                  </div>

                  <div className="sc-actions d-flex align-items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      className="sc-btn sc-btn--ghost"
                      onClick={() => handleDuplicate(plan)}
                      title="Duplicate customization"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Duplicate
                    </button>
                    <button
                      type="button"
                      className="sc-icon-btn"
                      onClick={() => handleEditPlan(plan)}
                      title="Edit Plan Customization"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="sc-icon-btn sc-icon-btn--danger"
                      onClick={() => deletePlanCustomization(plan._id)}
                      title="Delete Plan Customization"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="sc-btn sc-btn--primary ms-1"
                      onClick={() => handleNavigate(plan._id)}
                    >
                      Customize
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="ms-1">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="sc-empty d-flex flex-column align-items-center text-center py-5">
              <div className="sc-empty-icon d-flex align-items-center justify-content-center mb-3">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <div className="fw-semibold text-dark mb-1" style={{ fontSize: "0.9375rem" }}>
                No SaaS plans customized yet
              </div>
              <div className="text-secondary" style={{ fontSize: "0.8125rem" }}>
                Use the "Add customization" button to create your first plan customization.
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .sc-back-btn { transition: color 0.15s ease; }
        .sc-back-btn:hover { color: #1f2937 !important; }
        .sc-back-btn:hover svg { transform: translateX(-2px); }
        .sc-back-btn svg { transition: transform 0.15s ease; }

        .sc-card {
          background: #fff;
          border: 1px solid #ececec;
          border-radius: 12px;
          padding: 16px 18px;
          transition: border-color 0.15s ease, box-shadow 0.2s ease;
        }
        .sc-card:hover {
          border-color: #dcdcdc;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04), 0 4px 12px rgba(17, 24, 39, 0.04);
        }
        .sc-card-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: -0.005em;
        }

        .sc-plan-badge {
          padding: 4px 10px;
          font-size: 0.75rem;
          color: #4338ca;
          background: #eef2ff;
          border: 1px solid #e0e7ff;
          border-radius: 999px;
          line-height: 1.4;
        }
        .sc-plan-label {
          font-weight: 500;
        }
        .sc-plan-divider {
          margin: 0 6px;
          color: #a5b4fc;
        }
        .sc-plan-name {
          color: #312e81;
          font-weight: 600;
        }

        .sc-actions { gap: 6px !important; }

        .sc-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.005em;
          padding: 8px 14px;
          border-radius: 8px;
          border: 1px solid transparent;
          line-height: 1;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
          font-family: inherit;
        }
        .sc-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }

        .sc-btn--ghost {
          background: #fff;
          border-color: #e5e7eb;
          color: #1f2937;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
        }
        .sc-btn--ghost:hover {
          background: #fafafa;
          border-color: #d1d5db;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06);
        }
        .sc-btn--ghost:active { transform: translateY(0); }

        .sc-btn--primary {
          background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
          border-color: rgba(255, 255, 255, 0.04);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(79, 70, 229, 0.22),
            0 4px 10px rgba(79, 70, 229, 0.2);
        }
        .sc-btn--primary:hover {
          background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(67, 56, 202, 0.28),
            0 8px 18px rgba(79, 70, 229, 0.3);
        }
        .sc-btn--primary:hover svg { transform: translateX(2px); }
        .sc-btn--primary:active { transform: translateY(0); }
        .sc-btn--primary svg { transition: transform 0.15s ease; }

        .sc-icon-btn {
          width: 32px;
          height: 32px;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #4b5563;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
          transition: transform 0.15s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.2s ease;
        }
        .sc-icon-btn:hover {
          background: #fafafa;
          border-color: #d1d5db;
          color: #1f2937;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 4px 10px rgba(17, 24, 39, 0.06);
        }
        .sc-icon-btn:active { transform: translateY(0); }
        .sc-icon-btn:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.2);
        }
        .sc-icon-btn--danger:hover {
          color: #b91c1c;
          background: #fef2f2;
          border-color: #fecaca;
          box-shadow: 0 2px 6px rgba(185, 28, 28, 0.1);
        }

        .sc-empty {
          border: 1px dashed #e5e5e5;
          border-radius: 12px;
          background: #fafafa;
        }
        .sc-empty-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #fff;
          border: 1px solid #ececec;
          color: #9ca3af;
        }

        /* Delete-confirmation dialog (SweetAlert2 customization) */
        .sc-swal-popup {
          border-radius: 14px !important;
          border: 1px solid #ececec;
          font-family: 'Inter', sans-serif !important;
          padding: 6px 4px 14px !important;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18) !important;
          width: 420px !important;
        }
        .swal2-icon.sc-swal-icon {
          width: 56px !important;
          height: 56px !important;
          border-width: 1px !important;
          border-color: #fecaca !important;
          background: #fef2f2 !important;
          color: #dc2626 !important;
          margin: 18px auto 6px !important;
          display: flex !important;
          align-items: center;
          justify-content: center;
        }
        .swal2-icon.sc-swal-icon .swal2-icon-content {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0;
        }
        .swal2-icon.sc-swal-icon .swal2-icon-content svg { stroke: #dc2626; }
        .sc-swal-title {
          font-size: 1.0625rem !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          letter-spacing: -0.005em;
          padding: 4px 16px 0 !important;
          margin: 0 !important;
        }
        .sc-swal-text {
          font-size: 0.875rem !important;
          color: #6b7280 !important;
          line-height: 1.5 !important;
          padding: 8px 24px 0 !important;
          margin: 0 !important;
        }
        .sc-swal-actions {
          margin: 22px 16px 4px !important;
          gap: 8px !important;
          justify-content: flex-end !important;
        }
        .sc-swal-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8125rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.005em;
          padding: 9px 16px !important;
          border-radius: 9px !important;
          border: 1px solid transparent !important;
          line-height: 1;
          cursor: pointer;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
          margin: 0 !important;
        }
        .sc-swal-btn:focus {
          outline: none !important;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.18) !important;
        }
        .sc-swal-btn--ghost {
          background: #fff !important;
          border-color: #e5e7eb !important;
          color: #1f2937 !important;
          box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04) !important;
        }
        .sc-swal-btn--ghost:hover {
          background: #fafafa !important;
          border-color: #d1d5db !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(17, 24, 39, 0.05), 0 6px 14px rgba(17, 24, 39, 0.06) !important;
        }
        .sc-swal-btn--ghost:active { transform: translateY(0); }
        .sc-swal-btn--danger {
          background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%) !important;
          color: #fff !important;
          border-color: rgba(255, 255, 255, 0.04) !important;
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 1px 2px rgba(185, 28, 28, 0.22),
            0 4px 10px rgba(220, 38, 38, 0.2) !important;
        }
        .sc-swal-btn--danger:hover {
          background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 1px 0 rgba(255, 255, 255, 0.18) inset,
            0 2px 4px rgba(153, 27, 27, 0.28),
            0 8px 18px rgba(220, 38, 38, 0.3) !important;
        }
        .sc-swal-btn--danger:active { transform: translateY(0); }
      `}</style>
    </div>
  );
}
